"use client";

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import { useAutocomplete } from "@/hooks/use-search";
import type { AutocompleteResult } from "@/lib/api/types";
import { formatCompactCount } from "@/lib/string/format-count";
import { titleCasePreserveAcronyms, titleCaseStrictSmallWords } from "@/lib/string/title-case";
import { Select } from "@/components/ui/select";
import type { InputVariantProps } from "@/components/ui/input";
import { LOGO_DEV_TOKEN, logo } from "@/lib/logo-dev";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "@/components/ui/icon";

// Only fields whose values are organizations get logo.dev avatars; anything
// else (titles, enums, series codes, clearance levels, ...) would get a
// logo guessed from the text.
const LOGO_FIELDS = new Set(["companies", "universities"]);

export function fieldShowsLogos(field: string): boolean {
  return LOGO_FIELDS.has(field);
}

const CATEGORY_LABELS: Record<string, string> = {
  country: "COUNTRIES",
  region: "REGIONS",
  city: "CITIES",
};

function formatLabel(field: string, value: string): string {
  if (field === "companies" || field === "universities") return titleCasePreserveAcronyms(value);
  if (field === "job_titles") return titleCaseStrictSmallWords(value);
  return value;
}

const EMPTY_AUTOCOMPLETE_RESULTS: AutocompleteResult[] = [];

interface StaticItem {
  value: string;
  label: string;
  icon?: string;
  /** Groups static items under a header, like API result categories. */
  category?: string;
}

interface AutocompleteInputProps {
  field: string;
  placeholder: string;
  onSelect: (result: AutocompleteResult) => void;
  onFreeText?: (text: string) => void;
  allowFreeText?: boolean;
  flatBottom?: boolean;
  showAllOnOpen?: boolean;
  staticItems?: StaticItem[];
  selectedValues?: string[];
  /** Applied to `Select.Trigger` (input shell). */
  variant?: InputVariantProps["variant"];
  size?: InputVariantProps["size"];
  /**
   * Pass `false` when the input already lives inside a mobile drawer — the
   * responsive `Select` would open a second drawer over it and dismiss the
   * first on the initial pick.
   */
  responsive?: boolean;
  /**
   * Commit the typed text as free text on Enter when no result is highlighted
   * (by default Enter only picks a highlighted row).
   */
  enterCommitsFreeText?: boolean;
  disabled?: boolean;
}

export function AutocompleteInput({
  field,
  placeholder,
  onSelect,
  onFreeText,
  allowFreeText = true,
  flatBottom = false,
  showAllOnOpen = false,
  staticItems,
  selectedValues,
  variant,
  size,
  responsive,
  enterCommitsFreeText = false,
  disabled = false,
}: AutocompleteInputProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  const enableFetch = !staticItems && (showAllOnOpen
    ? isOpen
    : isOpen && debouncedQuery.length >= 1);

  const { data: rawResults, isFetching: apiFetching } = useAutocomplete(
    field,
    debouncedQuery,
    enableFetch,
    showAllOnOpen,
  );

  const staticLabelMap = useMemo(() => {
    if (!staticItems) return null;
    return new Map(
      staticItems.map((item) => [item.value, { label: item.label, icon: item.icon }]),
    );
  }, [staticItems]);

  const staticResults = useMemo((): AutocompleteResult[] | undefined => {
    if (!staticItems) return undefined;
    const q = debouncedQuery.toLowerCase();
    const filtered = q
      ? staticItems.filter((item) => item.label.toLowerCase().includes(q))
      : staticItems;
    return filtered.map((item) => ({
      value: item.value,
      count: 0,
      domain: null,
      category: item.category ?? null,
      canonical_id: null,
      lat: null,
      lon: null,
    }));
  }, [staticItems, debouncedQuery]);

  const isFetching = staticItems ? false : apiFetching;

  const results = useMemo(() => {
    if (staticItems) return staticResults;
    if (showAllOnOpen && !debouncedQuery && rawResults) {
      return [...rawResults].sort((a, b) => a.value.localeCompare(b.value));
    }
    return rawResults;
  }, [staticItems, staticResults, showAllOnOpen, debouncedQuery, rawResults]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [results]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleSelect = useCallback(
    (result: AutocompleteResult) => {
      onSelect(result);
      setQuery("");
      setDebouncedQuery("");
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onSelect],
  );

  const handleFreeText = useCallback(
    (text: string) => {
      onFreeText?.(text);
      setQuery("");
      setDebouncedQuery("");
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onFreeText],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    }
  }, []);

  const showAskArbi = query.trim().length > 0;
  const items = results ?? EMPTY_AUTOCOMPLETE_RESULTS;
  const showLogos = fieldShowsLogos(field) && !!LOGO_DEV_TOKEN;
  const selectedValueSet = useMemo(
    () => new Set(selectedValues ?? []),
    [selectedValues],
  );

  const showDropdown = isOpen;

  const showMustType =
    debouncedQuery.length < 1 &&
    !showAskArbi &&
    !showAllOnOpen &&
    !staticItems;

  const itemIndexOffset = showAskArbi ? 1 : 0;

  return (
    <Select.Root
      open={showDropdown}
      onOpenChange={handleOpenChange}
      highlightedIndex={highlightedIndex}
      onHighlightedIndexChange={setHighlightedIndex}
      responsive={responsive}
    >
      <div className="relative w-full">
        <Select.Trigger
          className={cn(
            flatBottom && "rounded-b-none border-b-0 shadow-none",
            disabled && "cursor-not-allowed opacity-50",
          )}
          variant={variant}
          size={size}
          disabled={disabled}
        >
          <span className="flex-1 text-sm text-contrast/50 text-left">
            {placeholder}
          </span>
          <div className="flex items-center justify-center size-5 shrink-0 opacity-40">
            <Icon name="IconChevronGrabberVertical" size={18} className="text-heading" />
          </div>
        </Select.Trigger>

        <Select.Content>
          <div className="flex items-center h-9 px-2.5 border-b border-contrast/8 shrink-0">
            <Icon
              name="IconMagnifyingGlass"
              size={14}
              className="text-contrast opacity-40 shrink-0 mr-2"
            />
            <Select.Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (
                  enterCommitsFreeText &&
                  allowFreeText &&
                  e.key === "Enter" &&
                  highlightedIndex < 0 &&
                  query.trim()
                ) {
                  handleFreeText(query.trim());
                }
              }}
              placeholder={placeholder}
            />
            {isFetching && (
              <Icon
                name="IconLoadingCircle"
                size={14}
                className="text-heading opacity-40 animate-spin shrink-0"
              />
            )}
          </div>
          <Select.List>
            {showMustType ? (
              <Select.ListMustType />
            ) : (
              <>
                {showAskArbi ? (
                  <Select.AskArbi index={0} onSelect={handleFreeText} />
                ) : null}
                {items.map((result, i) => {
                  const prev = i > 0 ? items[i - 1] : undefined;
                  const showCategoryHeader =
                    Boolean(result.category) && result.category !== prev?.category;
                  const itemIndex = itemIndexOffset + i;
                  const isSelected = selectedValueSet.has(result.value);
                  const itemLogoSrc = showLogos
                    ? logo({ domain: result.domain, name: result.value, size: 16, format: "png" })
                    : null;
                  const staticMeta = staticLabelMap?.get(result.value);
                  const staticIcon = staticMeta?.icon;

                  return (
                    <Fragment
                      key={`${(result as AutocompleteResult).canonical_id ?? "ft"}-${result.value}`}
                    >
                      {showCategoryHeader ? (
                        <Select.CategoryHeader>
                          {CATEGORY_LABELS[result.category!] ?? result.category!.toUpperCase()}
                        </Select.CategoryHeader>
                      ) : null}
                      <Select.Item
                        index={itemIndex}
                        selected={isSelected}
                        onSelect={() => handleSelect(result)}
                      >
                        {staticIcon ? (
                          <Select.ItemIcon>
                            <div className="flex size-4 shrink-0 items-center justify-center">
                              <Icon
                                name={staticIcon as IconName}
                                size={12}
                                className="shrink-0 text-contrast opacity-50"
                              />
                            </div>
                          </Select.ItemIcon>
                        ) : itemLogoSrc ? (
                          <Select.ItemAvatar src={itemLogoSrc} />
                        ) : null}
                        <Select.ItemLabel selected={isSelected} className="max-w-52 text-left">
                          {staticMeta?.label ?? formatLabel(field, result.value)}
                        </Select.ItemLabel>
                        {isSelected ? (
                          <Select.ItemCheck />
                        ) : result.count > 0 ? (
                          <Select.ItemCount>
                            {formatCompactCount(result.count)}
                          </Select.ItemCount>
                        ) : null}
                      </Select.Item>
                    </Fragment>
                  );
                })}
                {isFetching && !items.length ? <Select.ListPending /> : null}
                {!isFetching && debouncedQuery.length >= 1 && !items.length && !showAskArbi ? (
                  <Select.ListEmpty />
                ) : null}
              </>
            )}
          </Select.List>
        </Select.Content>
      </div>
    </Select.Root>
  );
}
